import { forwardRef, useEffect, useRef } from "react";
import "./AudioLevelIndicator.css";

export interface AudioLevelIndicatorProps {
  /** Audio level from 0-1 (normalized RMS amplitude) */
  level: number;
  /** Whether the indicator is active/recording */
  isActive: boolean;
  /** Whether speech is currently detected (vs silence/noise) */
  isSpeechDetected?: boolean;
  /** Visual variant of the indicator */
  variant?: "bar" | "dots" | "waveform";
  /** Size variant */
  size?: "sm" | "md";
  /** Additional CSS class name */
  className?: string;
  /** Color for waveform bars (CSS color value) */
  color?: string;
}

/**
 * AudioLevelIndicator component
 *
 * Displays real-time audio level feedback for dictation/recording.
 * Provides visual feedback that the microphone is working and
 * speech is being detected.
 *
 * @example
 * ```tsx
 * <AudioLevelIndicator
 *   level={0.5}
 *   isActive={true}
 *   isSpeechDetected={true}
 * />
 * ```
 */
export const AudioLevelIndicator = forwardRef<
  HTMLDivElement,
  AudioLevelIndicatorProps
>(
  (
    {
      level,
      isActive,
      isSpeechDetected = false,
      variant = "bar",
      size = "sm",
      className,
      color,
    },
    ref,
  ) => {
    // Normalize and clamp level to 0-1 range
    const normalizedLevel = Math.max(0, Math.min(1, level));

    // Scale the level for better visual representation
    // RMS values are typically low (0.01-0.1 for speech), so we amplify
    const scaledLevel = Math.min(1, normalizedLevel * 10);

    const classes = [
      "audio-level-indicator",
      `audio-level-indicator--${variant}`,
      `audio-level-indicator--${size}`,
      isActive ? "audio-level-indicator--active" : "",
      isSpeechDetected ? "audio-level-indicator--speech" : "",
      className || "",
    ]
      .filter(Boolean)
      .join(" ");

    if (variant === "waveform") {
      return (
        <div
          ref={ref}
          className={classes}
          role="status"
          aria-label="Audio level indicator"
        >
          <WaveformIndicator
            level={scaledLevel}
            isActive={isActive}
            color={color}
          />
        </div>
      );
    }

    if (variant === "dots") {
      return (
        <div
          ref={ref}
          className={classes}
          role="status"
          aria-label="Audio level indicator"
        >
          <DotsIndicator
            level={scaledLevel}
            isActive={isActive}
            isSpeechDetected={isSpeechDetected}
          />
        </div>
      );
    }

    // Default: bar variant
    return (
      <div
        ref={ref}
        className={classes}
        role="status"
        aria-label="Audio level indicator"
      >
        <div
          className="audio-level-indicator__bar"
          style={{
            transform: `scaleX(${isActive ? scaledLevel : 0})`,
          }}
        />
      </div>
    );
  },
);

AudioLevelIndicator.displayName = "AudioLevelIndicator";

/**
 * Dots variant - animated dots that respond to audio level
 */
function DotsIndicator({
  level,
  isActive,
  isSpeechDetected: _isSpeechDetected, // Styling handled by parent's CSS class
}: {
  level: number;
  isActive: boolean;
  isSpeechDetected: boolean;
}) {
  // Thresholds tuned for typical speech RMS values (scaled by 10x)
  const thresholds = [0.05, 0.15, 0.3, 0.5, 0.7];

  return (
    <div className="audio-level-indicator__dots">
      {thresholds.map((threshold, index) => {
        const isLit = isActive && level >= threshold;
        const classes = [
          "audio-level-indicator__dot",
          isLit ? "audio-level-indicator__dot--active" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return <div key={index} className={classes} />;
      })}
    </div>
  );
}

/**
 * Waveform variant - mirrored bars that create an audio waveform effect
 * Bars extend both up and down from center line like a classic audio visualization
 */
function WaveformIndicator({
  level,
  isActive,
  color = "currentColor", // Inherits from parent (e.g., button text color)
}: {
  level: number;
  isActive: boolean;
  color?: string;
}) {
  const NUM_BARS = 20;
  const levelHistoryRef = useRef<number[]>(
    Array.from({ length: NUM_BARS }, () => 0),
  );
  const animationFrameRef = useRef<number>();
  const barsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isActive) {
      // Reset history when inactive
      levelHistoryRef.current = Array.from({ length: NUM_BARS }, () => 0);
      return;
    }

    const updateBars = () => {
      // Shift history left and add new level at the end
      const history = levelHistoryRef.current;
      for (let i = 0; i < NUM_BARS - 1; i++) {
        history[i] = history[i + 1];
      }
      // Add some variation to make it look more organic
      const variation = 0.3 + Math.random() * 0.7;
      history[NUM_BARS - 1] = level * variation;

      // Update bar scales directly via DOM for performance
      // Using scaleY keeps bars centered (transform-origin defaults to center)
      if (barsRef.current) {
        const bars = barsRef.current.children;
        for (let i = 0; i < bars.length; i++) {
          const bar = bars[i] as HTMLElement;
          const barLevel = history[i];
          // Min scale of 0.05, max of 1
          const scale = Math.max(0.05, barLevel);
          bar.style.transform = `scaleY(${scale})`;
        }
      }

      animationFrameRef.current = requestAnimationFrame(updateBars);
    };

    animationFrameRef.current = requestAnimationFrame(updateBars);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, level]);

  return (
    <div
      ref={barsRef}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1px",
        height: "20px",
        width: "32px",
      }}
    >
      {Array.from({ length: NUM_BARS }).map((_, index) => (
        <div
          key={index}
          style={{
            width: "1px",
            height: "100%",
            backgroundColor: color,
            borderRadius: "1px",
            transform: "scaleY(0.05)",
            transition: "transform 0.05s ease-out",
          }}
        />
      ))}
    </div>
  );
}
